import { Injectable } from '@angular/core';
import { Config, ToastController } from '@ionic/angular';
import { alertCircle, checkmarkCircle, informationCircle, warning, wifi } from 'ionicons/icons';
import { IonicColors } from '../../../shared/enums/ionicColors.enum';

@Injectable({
  providedIn: 'root'
})
export class ToastService {

  constructor(
    private toastController: ToastController,
    private config: Config
  ) { }

  /**
   * @description Funzione che si occupa di mostrare un toast di Ionic, vedi descrizione parametri per ulteriori informazioni
   * @param message Testo del toast
   * @param color Colore del toast, prendere come riferimento l'enum IonicColors
   * @param duration Definisce la durata di visualizzazione del toast
   */
  async showToast(
    message: string,
    color: IonicColors = IonicColors.GREEN,
    duration: number = 3000,
    options: IToastOptions = {}
  ) {
    const semanticColor = this.normalizeToastColor(color);
    const cssClass = ['app-toast', `app-toast--${semanticColor}`];

    if (this.isManagedAlertToast(message)) {
      cssClass.push('app-toast--managed-alert');
      cssClass.push('app-toast--centered', 'app-toast--iconless');
    }

    if (options.centerText) {
      cssClass.push('app-toast--centered');
    }

    if (options.hideIcon) {
      cssClass.push('app-toast--iconless');
    }

    if (options.cssClass) {
      cssClass.push(...(Array.isArray(options.cssClass) ? options.cssClass : [options.cssClass]));
    }

    const toast = await this.toastController.create({
      message: options.plainText && this.config.getBoolean('innerHTMLTemplatesEnabled')
        ? message.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
        : message,
      duration,
      position: "bottom",
      color: semanticColor,
      mode: "ios",
      icon: options.hideIcon ? undefined : this.getToastIcon(semanticColor),
      cssClass
    });
    await toast.present();
  }

  private normalizeToastColor(color: IonicColors): IonicColors {
    return color || IonicColors.GREEN;
  }

  private getToastIcon(color: IonicColors): string {
    const iconByColor: Record<string, string> = {
      [IonicColors.GREEN]: checkmarkCircle,
      [IonicColors.RED]: alertCircle,
      [IonicColors.YELLOW]: warning,
      [IonicColors.BLACK]: informationCircle,
      [IonicColors.GREY]: wifi,
      [IonicColors.LIGHT_GRAY]: informationCircle,
      [IonicColors.PRIMARY]: informationCircle,
      tertiary: warning
    };

    return iconByColor[color] || informationCircle;
  }

  private isManagedAlertToast(message: string): boolean {
    const normalizedMessage = (message || '').trim().toLowerCase();
    return normalizedMessage === 'allerta gestita' || normalizedMessage === 'alert managed';
  }
}

export interface IToastOptions {
  plainText?: boolean;
  centerText?: boolean;
  hideIcon?: boolean;
  cssClass?: string | string[];
}
